import type { Facet, FacetValue } from 'src/components/search/FilterV2/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { FIXED_ELEMENTS_PLACEHOLDER_ID } from 'src/components/common/Header/HeaderMobile/HeaderMobileFixed'
import NewIconFilter from 'src/components/Icons/NewIconFilter'
import { normalizeString } from 'src/utils/normalizeString'

import '../filters/quick-filters-mobile.scss'

import QuickFilterButtonV3 from './QuickFilterButtonV3'

type QuickFiltersV3Props = {
  setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>
  isFilterOpen: boolean
  filteredFacets: number
  handleFilterClick: (facetValue: FacetValue) => void
  facetsv2?: Facet[]
}

type QuickFilterFacetTarget = {
  key: string
  value: string
}

type QuickFilterFacetValue = FacetValue & {
  facetValuesToApply?: QuickFilterFacetTarget[]
}

type FacetValueMatch = {
  facetKey: string
  value: FacetValue
}

function findFacetValueMatch(facets: Facet[], possibleValues: string[]) {
  const normalizedValues = possibleValues.map((value) =>
    normalizeString(value.toLowerCase())
  )

  for (const facet of facets) {
    for (const value of facet.values ?? []) {
      const normalizedValue = normalizeString(value.value.toLowerCase())
      const normalizedLabel = normalizeString(value.label.toLowerCase())

      if (
        normalizedValues.includes(normalizedValue) ||
        normalizedValues.includes(normalizedLabel)
      ) {
        return {
          facetKey: facet.key,
          value,
        }
      }
    }
  }

  return undefined
}

export function getQuickFiltersFacet(facets: Facet[]) {
  const facetBrandDecathlon = facets?.find(
    (facet) => facet.key === '77560' || facet.key === 'marca-propria'
  )

  const facetBrandDecathlonValue = facetBrandDecathlon?.values.find(
    (value) => value.value === '77654' || value.value === 'marcas-decathlon'
  )

  const discountPixFacet = facets?.find((facet) => facet.key === 'sellername')
  const discountPixValue = discountPixFacet?.values?.find(
    (value) => value.value === 'iguasport-ltda'
  )

  const blueProductFacet = facets?.find(
    (facet) => facet.name?.toLocaleLowerCase() === 'blueproduct'
  )

  const blueProductFacetValue = blueProductFacet?.values.find(
    (value) => value.label?.toLocaleLowerCase() === 'true'
  )

  const womenFacetMatch = findFacetValueMatch(facets, ['mulher', 'mulheres'])

  const menFacetMatch = findFacetValueMatch(facets, ['homem', 'homens'])

  const femaleFacetMatch = findFacetValueMatch(facets, ['feminino', 'feminina'])

  const maleFacetMatch = findFacetValueMatch(facets, ['masculino', 'masculina'])

  const boyFacetMatch = findFacetValueMatch(facets, ['menino', 'meninos'])

  const girlFacetMatch = findFacetValueMatch(facets, ['menina', 'meninas'])

  const audienceFacetKey =
    womenFacetMatch?.facetKey ??
    menFacetMatch?.facetKey ??
    boyFacetMatch?.facetKey ??
    girlFacetMatch?.facetKey ??
    'genero'

  const dedupeFacetValues = (values: QuickFilterFacetTarget[]) =>
    values.filter(
      (item, index, self) =>
        self.findIndex(
          (current) => current.key === item.key && current.value === item.value
        ) === index
    )

  const createQuickFilterFacetValue = ({
    shouldDisplay,
    base,
    fallback,
    selected,
    targets,
  }: {
    shouldDisplay: boolean
    base?: FacetValueMatch
    fallback: { key: string; value: string; label: string }
    selected: boolean
    targets: QuickFilterFacetTarget[]
  }): QuickFilterFacetValue | undefined => {
    if (!shouldDisplay) {
      return undefined
    }

    return {
      key: base?.facetKey ?? fallback.key,
      value: base?.value.value ?? fallback.value,
      label: base?.value.label ?? fallback.label,
      quantity: base?.value.quantity ?? 0,
      selected,
      facetValuesToApply: dedupeFacetValues(targets),
    }
  }

  // Cada botão de universo aciona exclusivamente o(s) facet(s) do seu
  // próprio universo de gênero (Mulher, Homem, ou Menino+Menina para
  // Infantil), sem acoplar automaticamente sexo biológico ou idade —
  // ver spec.md (SPDBR-5666) Assumption 4 e Acceptance Criteria P1-2.2.
  const womenGenderFacetValue = createQuickFilterFacetValue({
    shouldDisplay: [womenFacetMatch, femaleFacetMatch].some(Boolean),
    base: womenFacetMatch ?? femaleFacetMatch,
    fallback: {
      key: audienceFacetKey,
      value: 'mulher',
      label: 'Mulher',
    },
    selected: [
      womenFacetMatch?.value.selected,
      femaleFacetMatch?.value.selected,
    ].some(Boolean),
    targets: [
      {
        key:
          (womenFacetMatch ?? femaleFacetMatch)?.facetKey ?? audienceFacetKey,
        value: (womenFacetMatch ?? femaleFacetMatch)?.value.value ?? 'mulher',
      },
    ],
  })

  const menGenderFacetValue = createQuickFilterFacetValue({
    shouldDisplay: [menFacetMatch, maleFacetMatch].some(Boolean),
    base: menFacetMatch ?? maleFacetMatch,
    fallback: {
      key: audienceFacetKey,
      value: 'homem',
      label: 'Homem',
    },
    selected: [
      menFacetMatch?.value.selected,
      maleFacetMatch?.value.selected,
    ].some(Boolean),
    targets: [
      {
        key: (menFacetMatch ?? maleFacetMatch)?.facetKey ?? audienceFacetKey,
        value: (menFacetMatch ?? maleFacetMatch)?.value.value ?? 'homem',
      },
    ],
  })

  const childrenFacetValue = createQuickFilterFacetValue({
    shouldDisplay: true,
    base: boyFacetMatch ?? girlFacetMatch,
    fallback: {
      key: audienceFacetKey,
      value: 'menino',
      label: 'Infantil',
    },
    selected: Boolean(
      boyFacetMatch?.value.selected && girlFacetMatch?.value.selected
    ),
    targets: [
      {
        key: boyFacetMatch?.facetKey ?? audienceFacetKey,
        value: boyFacetMatch?.value.value ?? 'menino',
      },
      {
        key: girlFacetMatch?.facetKey ?? audienceFacetKey,
        value: girlFacetMatch?.value.value ?? 'menina',
      },
    ],
  })

  return {
    facetBrandDecathlon,
    facetBrandDecathlonValue,
    discountPixFacet,
    discountPixValue,
    blueProductFacet,
    blueProductFacetValue,
    womenGenderFacetValue,
    menGenderFacetValue,
    childrenFacetValue,
  }
}

function isElementOutOfViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()

  return (
    rect.bottom < 0 ||
    rect.top > window.innerHeight ||
    rect.right < 0 ||
    rect.left > window.innerWidth
  )
}

const QuickFiltersV3 = ({
  setIsFilterOpen,
  isFilterOpen,
  filteredFacets,
  facetsv2,
  handleFilterClick,
}: QuickFiltersV3Props) => {
  const [headerHeight, setHeaderHeight] = useState(0)
  const headerRef = useRef<HTMLDivElement>(null)
  const [usePortal, setUsePortal] = useState(false)
  const [animate, setAnimate] = useState(false)
  const [showFade, setShowFade] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const removeDocumentPointerListenersRef = useRef<(() => void) | null>(null)
  const scrollContainerPointerTeardownRef = useRef<(() => void) | null>(null)

  const {
    blueProductFacet,
    discountPixFacet,
    discountPixValue,
    facetBrandDecathlon,
    facetBrandDecathlonValue,
    blueProductFacetValue,
    womenGenderFacetValue,
    menGenderFacetValue,
    childrenFacetValue,
  } = getQuickFiltersFacet(facetsv2 ?? [])

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.getBoundingClientRect().bottom)
    }
  }, [])

  const bindScrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainerPointerTeardownRef.current?.()
    scrollContainerPointerTeardownRef.current = null
    ref.current = node

    if (!node) {
      return
    }

    let isPointerDown = false
    let hasDragged = false
    let startX = 0
    let startScrollLeft = 0

    const canScroll = () => node.scrollWidth > node.clientWidth

    const detachDocumentPointerListeners = () => {
      removeDocumentPointerListenersRef.current?.()
      removeDocumentPointerListenersRef.current = null
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!isPointerDown) {
        return
      }

      const deltaX = event.clientX - startX

      if (!hasDragged && Math.abs(deltaX) < 6) {
        return
      }

      hasDragged = true
      node.scrollLeft = startScrollLeft - deltaX
    }

    const handlePointerUp = () => {
      if (hasDragged) {
        const preventClick = (clickEvent: Event) => {
          clickEvent.preventDefault()
          clickEvent.stopPropagation()
          node.removeEventListener('click', preventClick, true)
        }

        node.addEventListener('click', preventClick, true)
        window.setTimeout(() => {
          node.removeEventListener('click', preventClick, true)
        }, 0)
      }

      isPointerDown = false
      hasDragged = false
      detachDocumentPointerListeners()
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!canScroll()) {
        return
      }

      detachDocumentPointerListeners()
      isPointerDown = true
      hasDragged = false
      startX = event.clientX
      startScrollLeft = node.scrollLeft
      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerUp)
      document.addEventListener('pointercancel', handlePointerUp)
      removeDocumentPointerListenersRef.current = () => {
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerUp)
        document.removeEventListener('pointercancel', handlePointerUp)
      }
    }

    node.addEventListener('pointerdown', handlePointerDown)
    scrollContainerPointerTeardownRef.current = () => {
      node.removeEventListener('pointerdown', handlePointerDown)
      detachDocumentPointerListeners()
    }
  }, [])

  useEffect(() => {
    return () => {
      scrollContainerPointerTeardownRef.current?.()
      scrollContainerPointerTeardownRef.current = null
    }
  }, [])

  useEffect(() => {
    let ticking = false

    function updateFiltersBar() {
      ticking = false

      const element = document.getElementById('product-listing__filters-bar')

      if (
        element &&
        window.scrollY > headerHeight &&
        isElementOutOfViewport(element)
      ) {
        setUsePortal(true)
        setAnimate(true)
      } else {
        setAnimate(false)
        setUsePortal(false)
      }
    }

    function handleScroll() {
      if (ticking) return

      ticking = true
      window.requestAnimationFrame(updateFiltersBar)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [headerHeight])

  const content = (
    <div className="w-full min-w-0 flex items-center md:mt-4 mb-4 overflow-hidden">
      <button
        data-testid="open-filter-button"
        aria-label="Open Filters"
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        className={classNames(
          `quick-filter-open-filters shrink-0 bg-white flex items-center justify-center rounded-round gap-2 font-inter font-medium leading-[21px] text-restructure-primary text-sm px-4 py-3 mr-2 h-[46px] border border-restructure-background-neutral-12`,
          { showFade }
        )}
      >
        <NewIconFilter width="16" height="16" />
        <span
          className={classNames('transition-[max-width] duration-300 ease', {
            // Hide the label only in sticky/portal mode. Toggling it on
            // horizontal chip scroll shrinks the button, which can reset
            // scrollLeft and flicker showFade open/closed.
            'max-w-0 overflow-hidden': usePortal,
          })}
        >
          Filtros Avançados
        </span>
        {filteredFacets > 0 ? (
          <span className="bg-restructure-background-neutral-12 text-white rounded-full w-5 h-5 text-[10px] text-center justify-center">
            {filteredFacets}
          </span>
        ) : null}
      </button>

      <div
        ref={bindScrollContainerRef}
        onScroll={() => setShowFade((ref.current?.scrollLeft ?? 0) > 0)}
        className={classNames(
          'no-scrollbar items-center filter-mobile-scroll-container flex flex-nowrap gap-2 flex-1 min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain select-none pr-4',
          {
            'pl-5 ml-5 border-l-sm border-checkoutDefaultBorder': !usePortal,
          }
        )}
      >
        <QuickFilterButtonV3
          facetKey={womenGenderFacetValue?.key ?? ''}
          facetValue={womenGenderFacetValue}
          onClick={handleFilterClick}
          label="Mulher"
        />
        <QuickFilterButtonV3
          facetKey={menGenderFacetValue?.key ?? ''}
          facetValue={menGenderFacetValue}
          onClick={handleFilterClick}
          label="Homem"
        />
        <QuickFilterButtonV3
          facetKey={childrenFacetValue?.key ?? ''}
          facetValue={childrenFacetValue}
          onClick={handleFilterClick}
          label="Infantil"
        />
        <QuickFilterButtonV3
          facetKey={discountPixFacet?.key ?? ''}
          facetValue={discountPixValue}
          onClick={handleFilterClick}
          label="Desconto pix"
        />
        <QuickFilterButtonV3
          facetKey={facetBrandDecathlon?.key ?? ''}
          facetValue={facetBrandDecathlonValue}
          onClick={handleFilterClick}
          label="Marcas Decathlon"
        />

        <QuickFilterButtonV3
          facetKey={blueProductFacet?.key ?? ''}
          facetValue={blueProductFacetValue}
          onClick={handleFilterClick}
          label="Melhor custo benefício"
        />
      </div>
    </div>
  )

  if (usePortal && typeof document !== 'undefined') {
    const container = document.getElementById(FIXED_ELEMENTS_PLACEHOLDER_ID)

    return createPortal(
      <div
        className={classNames(
          'quick-filter-mobile w-full bg-white pl-4 pr-0 py-1',
          { 'fixed top-0 left-0': !container },
          { animate }
        )}
      >
        {content}
      </div>,
      container ?? document.body
    )
  }

  return content
}

export default QuickFiltersV3
